import type { CallbackInterface } from 'recoil'

import type { ModalBulkFindReplaceState } from 'Pages/RecipientsList/model'

import {
  fetchFindReplacePreview,
  fetchPreviewDetails,
  invokeFindReplace,
} from 'Pages/RecipientsList/recoil/services/modalBulkFindReplace'
import {
  notifyEmptyFieldSuccess,
  notifyFindReplaceSuccess,
} from 'Pages/RecipientsList/recoil/services/notifications'
import { dynamicFieldsState } from 'Pages/RecipientsList/recoil/state/columns'
import { appliedSearchFiltersState } from 'Pages/RecipientsList/recoil/state/filters'
import {
  modalBulkFindReplaceCanPreviewState,
  modalBulkFindReplaceState,
} from 'Pages/RecipientsList/recoil/state/modalBulkFindReplace'
import { selectionState } from 'Pages/RecipientsList/recoil/state/results'

import type { PageStateType } from 'Types/state'

import { errorUpdater, pageState } from 'State/page'

import { logAppError } from 'Helpers/logger'

import { withDelay } from 'Helpers/delay'

export const modalBulkFindReplaceOpenCallback =
  ({ set }: CallbackInterface) =>
  async () => {
    set(modalBulkFindReplaceState, (prev) => ({ ...prev, show: true }))
  }

export const modalBulkFindReplaceCancelCallback =
  ({ reset }: CallbackInterface) =>
  async () => {
    reset(modalBulkFindReplaceState)
  }

export const modalBulkFindReplaceConfirmCallback =
  ({ set, reset, snapshot }: CallbackInterface) =>
  async () => {
    try {
      const [
        canPreview,
        { mode, checked },
        searchFilters,
        { FindColumn, FindText, FindCaseSensitive, FindType, ReplaceText },
      ] = await Promise.all([
        snapshot.getPromise(modalBulkFindReplaceCanPreviewState),
        snapshot.getPromise(selectionState),
        snapshot.getPromise(appliedSearchFiltersState),
        snapshot.getPromise(modalBulkFindReplaceState),
      ])

      if (!canPreview) {
        return
      }

      const idTask = await invokeFindReplace(
        {
          mode,
          checked,
          searchFilters,
        },
        {
          FindColumn,
          FindText,
          FindCaseSensitive,
          FindType,
          ReplaceText,
        },
      )

      notifyFindReplaceSuccess()

      //
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'modalFindReplaceConfirmCallback',
        appError: error,
      })

      set<PageStateType>(pageState, errorUpdater)
    }

    reset(modalBulkFindReplaceState)
  }

export const modalBulkFindReplaceChangeFieldCallback =
  ({ set }: CallbackInterface) =>
  async (f: keyof ModalBulkFindReplaceState, v: any) => {
    set<ModalBulkFindReplaceState>(modalBulkFindReplaceState, (prev) => ({
      ...prev,
      [f]: v,
    }))
  }

export const modalBulkFindReplaceChangeCallback =
  ({ set }: CallbackInterface) =>
  async (payload: Partial<ModalBulkFindReplaceState>) => {
    set(modalBulkFindReplaceState, (prev) => ({
      ...prev,
      ...payload,
    }))
  }

export const modalBulkFindReplaceToggleFieldCallback =
  ({ set }: CallbackInterface) =>
  async (f: 'FindCaseSensitive') => {
    set<ModalBulkFindReplaceState>(modalBulkFindReplaceState, (prev) => ({
      ...prev,
      [f]: !prev[f],
    }))
  }

export const modalBulkFindReplaceFetchPreviewCallback =
  ({ set, snapshot, reset }: CallbackInterface) =>
  async () => {
    const [
      canPreview,
      { mode, checked },
      searchFilters,
      { FindColumn, FindText, FindCaseSensitive, FindType, ReplaceText, show },
    ] = await Promise.all([
      snapshot.getPromise(modalBulkFindReplaceCanPreviewState),
      snapshot.getPromise(selectionState),
      snapshot.getPromise(appliedSearchFiltersState),
      snapshot.getPromise(modalBulkFindReplaceState),
    ])

    if (!canPreview) {
      set<ModalBulkFindReplaceState>(modalBulkFindReplaceState, (prev) => ({
        ...prev,
        previewStatus: 'initial',
        items: [],
        totalItems: 0,
      }))
      return
    }

    if (!show) {
      return
    }

    set<ModalBulkFindReplaceState>(modalBulkFindReplaceState, (prev) => ({
      ...prev,
      previewStatus: 'fetching',
    }))

    try {
      const idTask = await fetchFindReplacePreview(
        {
          mode,
          checked,
          searchFilters,
        },
        {
          FindColumn,
          FindText,
          FindCaseSensitive,
          FindType,
          ReplaceText,
        },
      )

      set<ModalBulkFindReplaceState>(modalBulkFindReplaceState, (prev) => ({
        ...prev,
        previewStatus: 'fetching',
        idTask,
        pollingIteration: 1,
      }))
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'modalFindReplaceConfirmCallback',
        appError: error,
      })

      set<PageStateType>(pageState, errorUpdater)

      reset(modalBulkFindReplaceState)
    }
  }

export const modalBulkFindReplacePollingPreviewCallback =
  ({ set, snapshot, reset }: CallbackInterface) =>
  async () => {
    const {
      idTask,
      pollingIteration,
      FindText,
      FindCaseSensitive,
      ReplaceText,
      FindType,
      show,
    } = await snapshot.getPromise(modalBulkFindReplaceState)

    if (!show) {
      reset(modalBulkFindReplaceState)
      return
    }

    if (idTask !== undefined) {
      try {
        const preview = await fetchPreviewDetails(idTask, pollingIteration)

        set<ModalBulkFindReplaceState>(modalBulkFindReplaceState, (prev) => ({
          ...prev,
          ...preview,
          previewFindText: FindText,
          previewFindCaseSensitive: FindCaseSensitive,
          previewReplaceText: ReplaceText,
          previewFindType: FindType,
        }))
      } catch (error) {
        console.error('### error', error)

        logAppError({
          Component: 'RecipientsList',
          Method: 'modalBulkFindReplacePollingPreviewCallback',
          appError: error,
        })

        set<PageStateType>(pageState, errorUpdater)

        reset(modalBulkFindReplaceState)
      }
    }
  }

export const modalBulkFindReplaceChangePageCallback =
  ({ set }: CallbackInterface) =>
  async (p: { pageNumber?: number; pageSize?: number }) => {
    set<ModalBulkFindReplaceState>(modalBulkFindReplaceState, (prev) => ({
      ...prev,
      previewStatus: 'paging',
    }))

    await withDelay(0, 250)

    set<ModalBulkFindReplaceState>(modalBulkFindReplaceState, (prev) => ({
      ...prev,
      ...p,
      previewStatus: 'ready',
    }))
  }

//  empty fields

export const modalBulkFindReplaceChangeEmptyFieldCallback =
  ({ set }: CallbackInterface) =>
  async (f: ModalBulkFindReplaceState['EmptyField']) => {
    set(modalBulkFindReplaceState, (prev) => ({
      ...prev,
      EmptyField: f,
      isEmptyFieldDirty: true,
    }))
  }

export const modalBulkFindReplaceEmptyConfirmCallback =
  ({ snapshot, set, reset }: CallbackInterface) =>
  async () => {
    const [{ mode, checked }, searchFilters, { EmptyField }, dynamicFields] =
      await Promise.all([
        snapshot.getPromise(selectionState),
        snapshot.getPromise(appliedSearchFiltersState),
        snapshot.getPromise(modalBulkFindReplaceState),
        snapshot.getPromise(dynamicFieldsState),
      ])

    try {
      await invokeFindReplace(
        {
          mode,
          checked,
          searchFilters,
        },
        {
          FindColumn: EmptyField,
          FindText: '',
          FindCaseSensitive: false,
          FindType: 'All',
          ReplaceText: '',
        },
      )

      const fieldName =
        dynamicFields.find((f) => `Field${f.id}` === EmptyField)?.description ??
        ''
      notifyEmptyFieldSuccess(fieldName)

      //
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'modalBulkFindReplaceEmptyConfirmCallback',
        appError: error,
      })

      set<PageStateType>(pageState, errorUpdater)
    }

    reset(modalBulkFindReplaceState)
  }
